import { Navigate, useOutlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BankOutlined,
  LogoutOutlined,
  SettingOutlined,
  CalendarOutlined,
  TeamOutlined,
  DashboardOutlined,
  AppstoreOutlined,
  PictureOutlined,
  CameraOutlined,
  ControlOutlined
} from '@ant-design/icons';
import { MenuProps, Layout, Menu, theme, Typography } from 'antd';
import { useIntl } from "react-intl";

const { Header, Footer, Sider } = Layout;
const { Title } = Typography;

type MenuItem = Required<MenuProps>['items'][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
  } as MenuItem;
}

export const SuperAdminLayout = () => {
  const { user, logout, isSuperAdmin } = useAuth();
  const outlet = useOutlet();
  const [collapsed, setCollapsed] = useState(false);
  const {
    token: { colorBgContainer },
  } = theme.useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();

  // Redirect to login if not logged in
  useEffect(() => {
    if (!user) {
      navigate("/sign-in", { replace: true });
    }
  }, [user, navigate]);

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  const items: MenuItem[] = useMemo(() => {
    const returnValue = [];

    // Only show these items for super admin
    returnValue.push(getItem(intl.formatMessage({ id: 'section.dashboardName' }) || 'Dashboard', 'dashboard', <DashboardOutlined />));
    returnValue.push(getItem(intl.formatMessage({ id: 'section.organisationsName' }) || 'Organisations', 'organisations', <BankOutlined />));
    returnValue.push(getItem(intl.formatMessage({ id: 'section.usersName' }) || 'Users', 'users', <TeamOutlined />));
    returnValue.push(getItem(intl.formatMessage({ id: 'section.eventsName' }) || 'Events', 'events', <CalendarOutlined />));
    returnValue.push(getItem(intl.formatMessage({ id: 'section.galleriesName' }) || 'Galleries', 'galleries', <PictureOutlined />));
    returnValue.push(getItem(intl.formatMessage({ id: 'section.settingsName' }) || 'Settings', 'settings', <SettingOutlined />));
    returnValue.push(getItem(intl.formatMessage({ id: 'section.systemSettingsName' }) || 'System Limits', 'system-settings', <ControlOutlined />));
    returnValue.push(getItem(intl.formatMessage({ id: 'section.featuresName' }) || 'Features', 'features', <AppstoreOutlined />));

    return returnValue;
  }, [intl]);

  const bottomItems: MenuItem[] = useMemo(() => {
    const returnValue = [];

    returnValue.push(getItem(intl.formatMessage({ id: 'section.signoutName' }) || 'Sign Out', 'signout', <LogoutOutlined />));

    return returnValue;
  }, [intl]);

  const onClick: MenuProps['onClick'] = (e) => {
    if (e.key === "signout") {
      handleLogout();
    } else if (e.key === 'dashboard') {
      navigate("/admin/dashboard", { replace: true });
    } else if (e.key === 'organisations') {
      navigate("/admin/organisations", { replace: true });
    } else if (e.key === 'users') {
      navigate("/admin/users", { replace: true });
    } else if (e.key === 'events') {
      navigate("/admin/events", { replace: true });
    } else if (e.key === 'galleries') {
      navigate("/admin/galleries", { replace: true });
    } else if (e.key === 'settings') {
      navigate("/admin/settings", { replace: true });
    } else if (e.key === 'system-settings') {
      navigate("/admin/system-settings", { replace: true });
    } else if (e.key === 'features') {
      navigate("/admin/features", { replace: true });
    }
  };

  const selectedKeys = useMemo(() => {
    const returnValue = [];
    const path = location.pathname;

    if (path.startsWith('/admin/dashboard')) {
      returnValue.push('dashboard');
    } else if (path.startsWith('/admin/organisations')) {
      returnValue.push('organisations');
    } else if (path.startsWith('/admin/users')) {
      returnValue.push('users');
    } else if (path.startsWith('/admin/events')) {
      returnValue.push('events');
    } else if (path.startsWith('/admin/galleries')) {
      returnValue.push('galleries');
    } else if (path.startsWith('/admin/settings')) {
      returnValue.push('settings');
    } else if (path.startsWith('/admin/system-settings')) {
      returnValue.push('system-settings');
    } else if (path.startsWith('/admin/features')) {
      returnValue.push('features');
    } else if (path === '/admin') {
      returnValue.push('dashboard');
    }

    return returnValue;
  }, [location.pathname]);

  // Redirect if not super admin
  if (!user) {
    return <Navigate to="/sign-in" />;
  }

  // Check if user is a super admin
  if (!isSuperAdmin()) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider collapsible collapsed={collapsed} onCollapse={(value) => setCollapsed(value)}>
        <div style={{ 
          padding: '16px', 
          textAlign: 'center', 
          margin: '10px 0'
        }}>
          <Link to="/admin/dashboard" style={{ display: 'inline-block' }}>
            {collapsed ? (
              <span style={{color: 'white'}}>
                <CameraOutlined/>
              </span>
            ) : (
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <span style={{color: 'white', fontSize: '20px', fontWeight: 'bold'}}>
                  <span style={{color: 'white', marginRight: '5px'}}>
                    <CameraOutlined/>
                  </span>
                  RecogniX Admin
                </span>
              </div>
            )}
          </Link>
        </div>
        <div>
          <div>
            <Menu
              onClick={onClick}
              theme="dark"
              selectedKeys={selectedKeys}
              mode="inline"
              items={items}
            />
          </div>
          <div>
            <Menu
              onClick={onClick}
              style={{marginTop: "2rem"}}
              theme="dark"
              mode="inline"
              items={bottomItems}
              selectable={false}
            />
          </div>
        </div>
      </Sider>
      <Layout>
        <Header style={{padding: 0, background: colorBgContainer}}>
          <Title level={4} style={{padding: '0 16px', margin: 0, lineHeight: '64px'}}>
            RecogniX Super Admin
          </Title>
        </Header>
        {outlet}
        <Footer style={{textAlign: 'center'}}>
          RecogniX.com.au © {new Date().getFullYear()}
        </Footer>
      </Layout>
    </Layout>
  );
};